import { dbOrders, dbUserAndHub } from "../../config/api";
import { Encrypt, encryptUserAndHubPayload } from "../../utils/encryption";

import axios from "axios";

/**
 * Business name fields from the order payload only (no BizId fallback).
 * @param {object} raw
 * @returns {string}
 */
export const getBizNameOnlyFromOrderRow = (raw) => {
  if (!raw || typeof raw !== "object") return "";
  const fromResponse =
    raw.BizName ??
    raw.bizName ??
    raw.storeName ??
    raw.StoreName ??
    "";
  return typeof fromResponse === "string" ? fromResponse.trim() : "";
};

/**
 * Label-friendly business title: name from response when present, otherwise BizId.
 * @param {object} raw
 * @returns {string}
 */
export const getBizDisplayNameFromOrderRow = (raw) => {
  const nameOnly = getBizNameOnlyFromOrderRow(raw);
  if (nameOnly) return nameOnly;
  const fallbackId = raw?.BizId ?? raw?.bizId;
  return fallbackId != null && fallbackId !== "" ? String(fallbackId) : "";
};

/**
 * @param {string} token
 * @param {{ bizId?: string, orderStatus?: string, isPrivateBiz?: boolean, cursor?: string }} [options] Optional filters narrow hub queries.
 */
export const intitialOrderFetch = async (token, options = {}) => {
  
  const body = {
    token,
    operationType: "read",
    action: "fetchAfterLogin",
    ...(options.bizId ? { BizId: options.bizId } : {}),
    ...(options.orderStatus ? { OrderStatus: options.orderStatus } : {}),
    ...(typeof options.isPrivateBiz === "boolean" ? { IsPrivateBiz: options.isPrivateBiz } : {}),
    ...(options.cursor ? { cursor: options.cursor } : {}),
  };
 
  return await axios.post(dbOrders, Encrypt(body), {
    headers: { "Content-Type": "application/json" },
  });
};

export const fetchOrderBusinessOptions = async (token) => {
  const response = await axios.post(
    dbUserAndHub,
    encryptUserAndHubPayload({
      operationType: "read",
      action: "fetchHubs",
      token,
    }),
    { headers: { "Content-Type": "application/json" } }
  );
  const data = response.data || {};
  const list = [];
  const seen = new Set();

  const pushStores = (hubMap) => {
    Object.entries(hubMap || {}).forEach(([hubKey, stores]) => {
      const hubId = String(hubKey || "").replace(/^HUB#/i, "").replace(/^HubId:/i, "");
      (stores || []).forEach((store) => {
        const bizId = store?.BizId;
        const activeFlag = String(store?.ActiveFlag || store?.activeFlag || "").toUpperCase();
        if (activeFlag === "DELETED") return;
        if (!bizId || seen.has(`${hubId}#${bizId}`)) return;
        seen.add(`${hubId}#${bizId}`);
        list.push({
          bizId,
          hubId,
          name: store?.BizName || bizId,
          bizPrivate: Boolean(store?.IsPrivate ?? store?.BizSettings?.bizPrivate),
        });
      });
    });
  };

  pushStores(data.adminHubs);
  pushStores(data.staffHubs);

  return list.sort((rowA, rowB) =>
    String(rowA.name || rowA.bizId).localeCompare(String(rowB.name || rowB.bizId), undefined, { sensitivity: "base" })
  );
};

/**
 * Fetch order line items. Same endpoint (OrderService).
 * Payload: token, operationType: "read", action: "fetchOrderItems", HubId, OrderId.
 */
export const fetchOrderItems = async (payload) => {
  const body = {
    token: payload.token,
    operationType: "read",
    action: "fetchOrderItems",
    HubId: payload.HubId,
    OrderId: payload.OrderId,
  };
  return await axios.post(dbOrders, Encrypt(body), {
    headers: { "Content-Type": "application/json" },
  });
};

/**
 * Add note on an order (OrderService). Hub token typically records as Biz.
 * Payload: operationType "update", action "updateOrderNote", OrderNotes: { notes }.
 */
export const updateOrderNote = async (payload) => {
  const body = {
    operationType: "update",
    action: "updateOrderNote",
    token: payload.token,
    HubId: payload.HubId,
    BizId: payload.BizId,
    OrderId: payload.OrderId,
    OrderNotes: {
      notes: payload.notes,
    },
  };
  return await axios.post(dbOrders, Encrypt(body), {
    headers: { "Content-Type": "application/json" },
  });
};

/**
 * True when the order has unread notes from the given author (`Cust` for admin, `Biz` for customer).
 */
export const hasUnreadOrderNotes = (orderNotes, authorBy) => {
  if (!Array.isArray(orderNotes) || !authorBy) return false;
  const expected = String(authorBy).trim().toLowerCase();
  return orderNotes.some(
    (note) => String(note?.by || "").trim().toLowerCase() === expected && note?.read !== true
  );
};

/**
 * Mark order notes as read (OrderService).
 * Payload: operationType "update", action "updateOrderNoteStatus", token, HubId, BizId, OrderId.
 */
export const updateOrderNoteStatus = async (payload) => {
  const body = {
    operationType: "update",
    action: "updateOrderNoteStatus",
    token: payload.token,
    HubId: payload.HubId,
    BizId: payload.BizId,
    OrderId: payload.OrderId,
  };
  return await axios.post(dbOrders, Encrypt(body), {
    headers: { "Content-Type": "application/json" },
  });
};

/** Remove note fields from order objects before status update (backend expects no OrderNotes on these payloads). */
const stripOrderNoteFieldsFromOrder = (order) => {
  if (!order || typeof order !== "object") return order;
  const { OrderNotes, OrderNote, ...rest } = order;
  return rest;
};

/**
 * Bulk update order status (OrderService).
 * Payload: token, operationType "update", action "updateOrderStatus",
 * orders: each object is the raw order from fetch + NewStatus (e.g. "Cancelled").
 * OrderNotes / OrderNote are omitted from each order before send.
 */
export const bulkUpdateOrderStatus = async ({ token, orders }) => {
  const body = {
    token,
    operationType: "update",
    action: "updateOrderStatus",
    orders: Array.isArray(orders)
      ? orders.map(stripOrderNoteFieldsFromOrder)
      : orders,
  };
  return await axios.post(dbOrders, Encrypt(body), {
    headers: { "Content-Type": "application/json" },
  });
};
